import Channel, { ChannelStore } from "@/types/Channel.interface"
import Message from "@/types/Message.interface"
import Person from "@/types/Person.interface"
import Pagination from "@/types/Pagination.interface"
import helpers from "@/utility/helpers"
import { MessageParams } from "@/types/PaginationParams.interface"
import User from "@/types/User.interface"
import Invitation from "@/types/Invitation.interface"

interface State {
    teammatesInvitations: Invitation[]             //  The invitations stored in our teammates view
    teammatesUsers: User[]                         //  The users displayed
    teammatesSelectedChannel: Channel              //  The teammates's currently selected channel
    teammatesSelectedChannelPaginating: boolean    //  The paginatiuon tracker for our selected channel
    teammatesSelectedInvitation: Invitation        //  The invitation currently selected
    teammatesSelectedMessages: Message[]           //  The messages displayed in the teammates
    teammatesSelectedMessagesLastPage: number      //  The last page of messages pagination (for current channel)
    teammatesSelectedMessagesPaginating: boolean   //  Determines if we want are currently paginating messages
    teammatesSelectedMessagesParams: MessageParams //  The pagination params for our messages
    teammatesSelectedPerson: Person                //  The person displayed in the teammates
    teammatesSelectedPersonPaginating: boolean     //  The person being paginated
    teammatesSelectedUser: User                    //  The user displayed in the teammates
    teammatesSelectedUserPaginating: boolean       //  The user being paginated
}

const defaults = () => {
    return {
        teammatesUsers: [],
        teammatesInvitations: [],
        teammatesSelectedChannel: null,
        teammatesSelectedChannelPaginating: false,
        teammatesSelectedInvitation: null,
        teammatesSelectedMessages: [],
        teammatesSelectedMessagesLastPage: null,
        teammatesSelectedMessagesPaginating: false,
        teammatesSelectedMessagesParams: {
            page: 1,
            limit: 20,
            filters: ['channel']
        },
        teammatesSelectedPerson: null,
        teammatesSelectedPersonPaginating: false,
        teammatesSelectedUser: null,
        teammatesSelectedUserPaginating: false
    }
}

export default {
    state: defaults(),
    mutations: {
        //  APP SYNC
        teammatesSelectedMessagesSync(state: State, messages: Message[]): void {
            //  Get the currently selected channel id
            const channel = state.teammatesSelectedChannel

            //  If a selected channel does NOT exist, we have nothing to do
            if (!channel) return

            //  Get messages that belong to the selected channel
            const msgs = messages.filter(x => x.channel_id == channel.id)

            //  Add each message to the messages list
            for (const x of msgs) helpers.addItemById(x, state.teammatesSelectedMessages, true)
        },
        teammatesMessagePost(state: State, message: Message): void {
            //  Get the currently selected channel
            const channel = state.teammatesSelectedChannel

            //  If a selected channel does NOT exist, we have nothing to do
            if (!channel) return

            //  Else, check if this message even belongs to this channel
            const valid = message.channel_id === channel.id

            //  If the message doesn't, then do nothing
            if (!valid) return

            //  @ts-ignore Update our channel last message and updated at
            channel.last_message_time = message.updated_at
            //  This might cause issues where the last message doesn't exist but i highly doubt it
            channel.last_message.text = message.text
            channel.updated_at = message.updated_at

            //  Update this channel
            state.teammatesSelectedChannel = channel

            //  Add this message to the list
            helpers.addItemById(message, state.teammatesSelectedMessages, true)
        },
        //  INVITATIONS
        teammatesInvitationsPush (state: State, invitations: Invitation[]): void {
            //  Update our state
            state.teammatesInvitations = window.removeDuplicatesById(state.teammatesInvitations, invitations)

            //  Get the currently selected invitation
            const selectedInvitation = state.teammatesSelectedInvitation

            //  If no selected invitation, do nothing
            if (!selectedInvitation) return

            //  Else, find if they were updated
            const updatedInvitation = invitations.find((x: Invitation) => x.id == selectedInvitation.id)

            //  If it wasn't, do nothing
            if (!updatedInvitation) return

            //  Else, update it
            state.teammatesSelectedInvitation = updatedInvitation
        },
        teammatesInvitationsRemove (state: State, ids: number[]): void {
            //  Get our current list of invitations
            const invitations = state.teammatesInvitations

            //  If no invitations, do nothing
            if (!invitations) return

            //  Else filter out the removed ids
            const filtered = invitations.filter((x: Invitation) => !ids.includes(x.id))

            //  Replace our users list with the filtered list
            state.teammatesInvitations = filtered

            //  Check if we have a selected invitation
            const selectedInvitation = state.teammatesSelectedInvitation

            //  If there is NO selected invitation, do nothing
            if (!selectedInvitation) return

            //  Else, check if this invitation was deleted
            const deleted = ids.find((x: number) => x == selectedInvitation.id)

            //  If it wasn't deleted do nothing
            if (!deleted) return

            //  Else, unset our invitation
            state.teammatesSelectedInvitation = null
        },
        teammatesInvitationsSet (state: State, invitations: Invitation[]): void {
            state.teammatesInvitations = invitations
        },
        //  USERS
        teammatesUsersPush (state: State, users: User[]): void {
            //  Update our state
            state.teammatesUsers = window.removeDuplicatesById(state.teammatesUsers, users)

            //  Get the currently selected user
            const selectedUser = state.teammatesSelectedUser

            //  If no selected user, do nothing
            if (!selectedUser) return

            //  Else, find if they were updated
            const updatedUser = users.find((x: User) => x.id == selectedUser.id)

            //  If it wasn't, do nothing
            if (!updatedUser) return

            //  Else, update it
            state.teammatesSelectedUser = updatedUser
        },
        teammatesUsersRemove (state: State, ids: number[]): void {
            //  Get our current list of users
            const users = state.teammatesUsers

            //  If no users, do nothing
            if (!users) return

            //  Else filter out the removed ids
            const filtered = users.filter((x: User) => !ids.includes(x.id))

            //  Replace our users list with the filtered list
            state.teammatesUsers = filtered

            //  Check if we have a selected user
            const selectedUser = state.teammatesSelectedUser

            //  If there is no selected user, do nothing
            if (!selectedUser) return

            //  Else, check if this user was deleted
            const deleted = ids.find((x: number) => x == selectedUser.id)

            //  If it wasn't deleted, do nothing
            if (!deleted) return

            //  Else, set our selected user and person to null
            state.teammatesSelectedUser = null
            state.teammatesSelectedPerson = null
        },
        teammatesUsersSet (state: State, users: User[]): void {
            state.teammatesUsers = users
        },
        teammatesSelectedChannelPaginatingSet(state: State, bool: boolean): void {
            state.teammatesSelectedChannelPaginating = bool
        },
        teammatesSelectedChannelRemove(state: State, ids: number[]): void {
            //  Get the currently selected channel
            const selected = state.teammatesSelectedChannel

            //  If the channel does NOT exist, we have nothing to do
            if (!selected) return

            //  Else, find if our channel was deleted
            const deleted = ids.find(x => x == selected.id)

            //  If the selected channel was NOT deleted, we have nothing to do
            if (!deleted) return

            //  Else, update the selected channel to null since deleted
            state.teammatesSelectedChannel = null
        },
        teammatesSelectedChannelSet(state: State, channel: Channel): void {
            state.teammatesSelectedChannel = channel
        },
        teammatesSelectedChannelSync(state: State, channels: Channel[]): void {
            //  Check if we have a selected channel
            const selected = state.teammatesSelectedChannel

            //  If we don't, do nothing
            if (!selected) return

            //  Else, check if our selected channel has been updated
            const updated = channels.find((x: Channel) => x.id === selected.id)

            //  If it hasn't been updated, do nothing
            if (!updated) return

            //  Else, update our channel
            state.teammatesSelectedChannel = updated
        },
        //  SELECTED INVITATION
        teammatesSelectedInvitationSet (state: State, invitation: Invitation): void {
            state.teammatesSelectedInvitation = invitation
        },
        //  SELECTED CHANNEL MESSAGES
        teammatesSelectedMessagesParamsReset(state: State): void {
            state.teammatesSelectedMessagesParams.page = 1
            //state.teammatesSelectedMessagesLastPage = null
        },
        teammatesSelectedMessagesParamsSet(state: State, params: MessageParams): void {
            state.teammatesSelectedMessagesParams = params
        },
        teammatesSelectedMessagesSet(state: State, messages: Message[]): void {
            state.teammatesSelectedMessages = messages
        },
        teammatesSelectedMessagesPropertyPush(state: State, message: Message): void {
            //  Grab our global messages
            const messages = state.teammatesSelectedMessages

            //  If no selected messages, do nothing
            if (!messages) return

            //  Find our messages from this
            const existing = messages.find((x: Message) => x.id === message.id)

            //  If it doesn't exist, do nothing
            if (!existing) return

            //  Else, update the messages (this is for reactivity)
            const updated = Object.assign({}, existing, message)

            //  Replace with the updated
            state.teammatesSelectedMessages = window.removeDuplicatesById(state.teammatesSelectedMessages, [updated])
        },
        teammatesSelectedMessagesPush(state: State, messages: Message[]): void {
            state.teammatesSelectedMessages = window.removeDuplicatesById(state.teammatesSelectedMessages, messages)
        },
        teammatesSelectedMessagesRemove(state: State, ids: number[]): void {
            //  Grab our current list of messages
            const messages = state.teammatesSelectedMessages

            //  If no messages, do nothing
            if (!messages) return

            //  Else, filter out our ids
            const filtered = messages.filter((x: Message) => !ids.includes(x.id))

            //  Replace our list with the filtered
            state.teammatesSelectedMessages = filtered
        },
        //  Sets the current channel id on our pagination params for messages
        teammatesSelectedMessagesChannelSet(state: State, channelId: number): void {
            state.teammatesSelectedMessagesParams.channel_id = channelId
        },
        teammatesSelectedMessagesPaginatingSet(state: State, bool: boolean): void {
            state.teammatesSelectedMessagesPaginating = bool
        },
        //  Sets the pagination params for our selected messages
        teammatesSelectedMessagesPaginationPush(state: State, page: Pagination): void {
            //  If this is page one, reset the messages list
            if (page.current_page === 1) state.teammatesSelectedMessages = []

            //  Set our pages
            state.teammatesSelectedMessagesLastPage = page.last_page
        },
        //  SELECTED PERSON
        teammatesSelectedPersonPaginatingSet(state: State, bool: boolean): void {
            state.teammatesSelectedPersonPaginating = bool
        },
        teammatesSelectedPersonRemove(state: State, ids: number[]): void {
            //  Get the currently selected person
            const selectedPerson = state.teammatesSelectedPerson

            //  If the person does NOT exist, we have nothing to do
            if (!selectedPerson) return

            //  Else, find if our person was deleted
            const deleted = ids.find(x => x == selectedPerson.id)

            //  If the selected person was NOT deleted, we have nothing to do
            if (!deleted) return

            //  Else, update the selected person to null since deleted
            state.teammatesSelectedPerson = null
        },
        teammatesSelectedPersonSet(state: State, person: Person): void {
            state.teammatesSelectedPerson = person
        },
        teammatesSelectedPersonSync(state: State, updates: Person[]): void {
            //  Grab our currently selected person
            const selectedPerson = state.teammatesSelectedPerson

            //  If we don't have a selected person, we are done
            if (!selectedPerson) return

            //  Find if our selected person is in the updates
            const updatedPerson = updates.find((x: Person) => x.id === selectedPerson.id)

            //  If we have not updated this person, do nothing
            if (!updatedPerson) return

            //  Else, replace our person
            state.teammatesSelectedPerson = updatedPerson
        },
        //  SELECTED USER
        teammatesSelectedUserPaginatingSet(state: State, bool: boolean): void {
            state.teammatesSelectedUserPaginating = bool
        },
        teammatesSelectedUserRemove(state: State, ids: number[]): void {
            //  Get the currently selected user
            const selectedUser = state.teammatesSelectedUser

            //  If the user does NOT exist, we have nothing to do
            if (!selectedUser) return

            //  Else, find if our user was deleted
            const deleted = ids.find(x => x == selectedUser.id)

            //  If the selected user was NOT deleted, we have nothing to do
            if (!deleted) return

            //  Else, update the selected user to null since deleted
            state.teammatesSelectedUser = null
        },
        teammatesSelectedUserSet(state: State, user: User): void {
            state.teammatesSelectedUser = user
        },
        teammatesSelectedUserSync(state: State, updates: User[]): void {
            //  Grab our currently selected user
            const selectedUser = state.teammatesSelectedUser

            //  If we don't have a selected user, we are done
            if (!selectedUser) return

            //  Find if our selected user is in the updates
            const updatedUser = updates.find((x: User) => x.id === selectedUser.id)

            //  If we have not updated this user, do nothing
            if (!updatedUser) return

            //  Else, replace our user
            state.teammatesSelectedUser = updatedUser
        },
        teammatesReset(state: State): void {
            Object.assign(state, defaults())
        }
    },
    actions: {
        teammatesSelectedChannelGet({ commit, getters }, channelId: number): Promise<void> {
            //  Start the 'paginating' tracker
            commit('teammatesSelectedChannelPaginatingSet', true)

            //  Assemble the API endpoint
            const url = `${getters.api}/channels/${channelId}`

            //  Send the request
            return window.axios.get(url).then(response => {
                //  Set the teammates selected channel
                commit('teammatesSelectedChannelSet', response.data)

                //  Stop the 'paginating' tracker
                commit('teammatesSelectedChannelPaginatingSet', false)

                return response.data
            }).catch(() => null)
        },
        teammatesSelectedInvitationGet ({ commit, getters }, invitationId: number): Promise<Invitation> {
            //  Grab the global invitations list
            const invitations = getters.invitations

            //  Check if this invitation exists in the global list
            const existingInvitation = invitations.find((x: Invitation) => x.id == invitationId)

            //  If there is an existing person, commit and return
            if (existingInvitation) return commit('teammatesSelectedInvitationSet', existingInvitation)

            //  else, assemble the API endpoint
            const url = `${getters.api}/invitations/${invitationId}`

            //  send the request
            return window.axios.get(url).then(response => {
                //  Set our selected invitation
                commit('teammatesSelectedInvitationSet', response.data)

                //  Add it to our global list
                commit('invitationsPush', [response.data])

                //  Return the invitation
                return response.data
            }).catch(() => null)
        },
        teammatesSelectedMessagesPaginate({ commit, getters }, channelId: number): Promise<void> {
            //  Grab our pagination params
            const p = getters.teammatesSelectedMessagesParams

            //  Attach our channel id
            p.channel_id = channelId

            //  If we're paginating the first page, we need to check the cache for the message
            if (p.page === 1) {
                //  Get the messages cache
                const cache = getters.messagesCache

                //  Search for the existing messages in the cache
                const channelStore: ChannelStore = cache.find(x => x.channelID == channelId)

                //  If we have cached messages, use them
                if (channelStore) {
                    //  Update teammates pagination params trackers
                    commit('teammatesSelectedMessagesPaginationPush', { current_page: 1, last_page: channelStore.lastPage })

                    //  Load messages from the channel store
                    commit('teammatesSelectedMessagesSet', channelStore.messages)

                    //  Update the channelStore 'loaded' timestamp
                    channelStore.loaded = Date.now()
                    commit('messagesSet', channelStore)

                    //  Do nothing else
                    return
                }
            }

            //  Else, we need to paginate, Start our pagination tracker
            commit('teammatesSelectedMessagesPaginatingSet', true)

            //  Assemble the API endpoint
            const url = `${getters.api}/messages?${window.toUrlParams(p)}`

            //  Send the request
            return window.axios.get(url).then(response => {
                //  Update pagination params
                commit('teammatesSelectedMessagesPaginationPush', response.data)

                //  Set the teammates selected messages
                commit('teammatesSelectedMessagesPush', response.data.data)

                //  If this is page one, we need to save it to local storage
                if (response.data.current_page === 1) {

                    //  Assemble the channel store
                    const store: ChannelStore = {
                        channelID: channelId,
                        lastPage: response.data.last_page,
                        messages: response.data.data,
                        loaded: Date.now()
                    }

                    //  Save the channel store
                    commit('messagesSet', store)
                }

                //  Stop the pagination tracker
                commit('teammatesSelectedMessagesPaginatingSet', false)

            }).catch(e => console.error("teammatesSelectedMessagesPaginate ERROR", e))
        },
        teammatesSelectedPersonGet({ commit, getters }, userId: number) {
            //  Get the global persons list
            const persons = getters.persons

            //  Search for the exsiting user in the global dataset
            const existingPerson = persons.find((x: Person) => x.user_id == userId)

            //  If we found an existing person, return it
            if (existingPerson) return commit('teammatesSelectedPersonSet', existingPerson)

            //  Start our paginating tracker for our person
            commit('teammatesSelectedPersonPaginatingSet', true)

            //  Else, assemble the API endpoint
            const url = `${getters.api}/users/${userId}/person`

            //  Send the request
            return window.axios.get(url).then(response => {
                //  Set the teammates selected channel
                commit('teammatesSelectedPersonSet', response.data)

                //  Add the channel to the global dataset
                commit('personsPush', [response.data])

                //  Stop our paginating tracker for our person
                commit('teammatesSelectedPersonPaginatingSet', false)

                return response.data
            }).catch(() => null)
        },
        teammatesSelectedUserGet({ commit, getters }, userId: number) {
            //  Get the global users list
            const users = getters.users

            //  Search for the exsiting users in the global dataset
            const existingUser = users.find(x => x.id == userId)

            //  If we found an existing user, return it
            if (existingUser) return commit('teammatesSelectedUserSet', existingUser)

            //  Start our paginating tracker for our user
            commit('teammatesSelectedUserPaginatingSet', true)

            //  Else, assemble the API endpoint
            const url = `${getters.api}/users/${userId}`

            //  Send the request
            return window.axios.get(url).then(response => {
                //  Set the teammates selected channel
                commit('teammatesSelectedUserSet', response.data)

                //  Add the channel to the global dataset
                commit('usersPush', [response.data])

                //  Stop our paginating tracker for our person
                commit('teammatesSelectedUserPaginatingSet', false)

                return response.data
            }).catch(() => null)
        },
        teammatesSelectedScheduledMessagesGet({ commit, getters }, channelId: number): Promise<void> {
            //  Assemble pagination params
            const params = {
                page: 1,
                limit: 100,
                channel_id: channelId,
                filters: ['scheduled']
            }

            //  Assemble the API endpoint
            const url = `${getters.api}/messages?${window.toUrlParams(params)}`

            //  Send the request
            return window.axios.get(url).then(response => {
                commit('teammatesSelectedScheduledMessagesSet', response.data.data)
            })
        }
    },
    getters: {
        teammatesInvitations(state: State): Invitation[] {
            return state.teammatesInvitations
        },
        teammatesUsers(state: State, getters): User[] {
            //  Get all of our users
            const users = state.teammatesUsers

            //  Filter out support users
            const filtered = users.filter((x: User) => {
                //  Grab the users role
                const role = getters.userRole(x)

                //  Exclude any where role is 4
                return role && role.permission !== 4
            })

            //  Return our filtered users
            return filtered
        },
        teammatesSelectedChannel(state: State): Channel {
            return state.teammatesSelectedChannel
        },
        teammatesSelectedChannelPaginating(state: State): boolean {
            return state.teammatesSelectedChannelPaginating
        },
        teammatesSelectedInvitation(state: State): Invitation {
            return state.teammatesSelectedInvitation
        },
        teammatesSelectedMessages(state: State): Message[] {
            return state.teammatesSelectedMessages
        },
        teammatesSelectedMessagesLastPage(state: State): number {
            return state.teammatesSelectedMessagesLastPage
        },
        teammatesSelectedMessagesPaginating(state: State): boolean {
            return state.teammatesSelectedMessagesPaginating
        },
        teammatesSelectedMessagesParams(state: State): MessageParams {
            return state.teammatesSelectedMessagesParams
        },
        teammatesSelectedPerson(state: State): Person {
            return state.teammatesSelectedPerson
        },
        teammatesSelectedPersonPaginating(state: State): boolean {
            return state.teammatesSelectedPersonPaginating
        },
        teammatesSelectedUser(state: State): User {
            return state.teammatesSelectedUser
        },
        teammatesSelectedUserPaginating(state: State): boolean {
            return state.teammatesSelectedUserPaginating
        }
    }
}